import React, { useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Toolbar,
} from "@mui/material";
import { ChevronLeft as ChevronLeftIcon } from "@mui/icons-material";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import "../../../styles/SideBar.css";
import Logo from "../../../assets/Atithiseva-Logo-Final-1024x298.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { adminMenuItems } from "./adminMenuItems";
import { vendorMenuItems } from "./vendorMenuItems";
import { customerMenuItems, customerMenuItemsWithoutBookings } from "./customerMenuItems";
import { useDispatch, useSelector } from "react-redux";
import {
  CHANGE_PASSWORD,
  DASHBOARD,
  INVOICES,
  LOGIN,
  NOTIFICATIONS,
  PROFILE_UPDATE,
  PROMOCODE,
  REPORTS,
} from "../Routes/AppRoutes";
import { CUSTOMER_BOOKINGS } from "../Routes/CustomerRoutes";
import { logoutRequest } from "../../../redux/auth/actions/authActions";
import { fetchCustomerDetailsRequest } from "../../../redux/Customers/actions/customerActions";

const filterMenuItemsByPermissions = (
  menuItems,
  permissions,
  userRole,
  user_role_name
) => {
  let parsedPermissions = permissions;
  if (typeof permissions === "string") {
    try {
      parsedPermissions = JSON.parse(
        permissions.replace(/\\"/g, '"').replace(/^"|"$/g, "")
      );
    } catch (error) {
      parsedPermissions = {};
    }
  }

  const filteredMenuItems = menuItems.filter((item) => {
    if (
      item.action === "logout" ||
      item.path === PROFILE_UPDATE ||
      item.path === CHANGE_PASSWORD ||
      item.path === DASHBOARD
    ) {
      return true;
    }

    if (
      user_role_name === "Manager" ||
      user_role_name === "Admin" ||
      user_role_name === "Assistant Manager"
    ) {
      if (item.path === PROMOCODE) {
        return true;
      }
    }

    if (item.path === NOTIFICATIONS && (userRole || "").toLowerCase() === "staff") {
      return true;
    }
    if (
      item.path === INVOICES ||
      item.path === REPORTS ||
      item.path === NOTIFICATIONS
    ) {
      const modulePermissions =
        parsedPermissions[item.path.toLowerCase().replace("/", "")];
      return modulePermissions?.allaccess || false;
    }

    const matchingPermission = Object.entries(parsedPermissions).find(
      ([key, value]) => {
        const pathLowerCase = item.path.toLowerCase();
        const keyLowerCase = key.toLowerCase();

        const pathMatches =
          pathLowerCase.includes(keyLowerCase) ||
          keyLowerCase.includes(pathLowerCase);

        return pathMatches;
      }
    );

    if (!matchingPermission) {
      return false;
    }

    const [permissionKey, permissionValue] = matchingPermission;

    const hasAccess =
      permissionValue.allaccess ||
      permissionValue.view ||
      permissionValue.add ||
      permissionValue.edit ||
      permissionValue.delete;

    return hasAccess;
  });

  return filteredMenuItems;
};

const getMenuItemsByRole = (role, permissions, customerDetailsState, user_role_name) => {

  let baseMenuItems;
  const normalizedRole = (role || "").toLowerCase();
  switch (normalizedRole) {
    case "admin":
      baseMenuItems = adminMenuItems;
      break;
    case "staff":
      baseMenuItems = adminMenuItems;
      break;
    case "vendor":
      baseMenuItems = vendorMenuItems;
      break;
    case "customer":
      baseMenuItems =customerDetailsState?.phone == null ? customerMenuItemsWithoutBookings:customerMenuItems;
      break;
    default:
      return [];
  }
  

  if (!permissions || (Array.isArray(permissions) && permissions.length === 0) || (typeof permissions === 'object' && Object.keys(permissions).length === 0)) {
    return baseMenuItems;
  }

  return filterMenuItemsByPermissions(
    baseMenuItems,
    permissions,
    role,
    user_role_name
  );
};

const SideBar = ({
  userRole,
  isDrawerOpen,
  handleDrawerToggle,
  permissions,
}) => {
  const user_role_name = useSelector((state) => state.auth?.user?.role_name);
 const user = useSelector((state) => state.auth.user);
   const navigate = useNavigate();
  const dispatch = useDispatch();
   const customerDetailsState = useSelector(
     (state) => state.customers.customerDetails?.data?.data
   );
 
   useEffect(() => {
      if (user) {
        dispatch(fetchCustomerDetailsRequest(user.customer_id));
      }
    }, [dispatch, user]);
 
  
  const location = useLocation();
  let menuItems = getMenuItemsByRole(userRole, permissions,customerDetailsState, user_role_name);

  if (userRole === 'customer' && user?.contact === null) {
    menuItems = menuItems.filter(item => item.path !== CUSTOMER_BOOKINGS);
  }



  const handleLogout = () => {
    dispatch(logoutRequest());
    localStorage.clear();
    navigate(LOGIN);
  };

  return (
    <Drawer
      variant="persistent"
      open={isDrawerOpen}
      sx={{
        width: isDrawerOpen ? 240 : 0,
        flexShrink: 0,
        position: { xs: "absolute", lg: "relative" },
        "& .MuiDrawer-paper": {
          width: isDrawerOpen ? 240 : 0,
          boxSizing: "border-box",
          backgroundColor: "#ffffff",
          color: "#000000",
          boxShadow: "2px 0px 8px rgba(0, 0, 0, 0.1)",
          transition: "width 0.3s ease",
        },
      }}
    >
      <Toolbar className="sidebar-toolbar">
        <IconButton onClick={handleDrawerToggle} className="sidebar-close-icon">
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>

      <Box className={`sidebar-logo ${isDrawerOpen ? "" : "hidden"}`}>
        <img src={Logo} alt="Logo" />
      </Box>

      <PerfectScrollbar
        options={{
          suppressScrollX: true,
        }}
        style={{
          maxHeight: "calc(100% - 100px)",
        }}
      >
        <List>
          {menuItems.map((item, index) => {
            const isActive =
              location.pathname === item.path ||
              (item.path !== LOGIN && location.pathname.startsWith(item.path));

            return (
              <React.Fragment key={index}>
                {item.action === "logout" ? (
                  <ListItem
                    button
                    className="sidebar-list-item"
                    onClick={handleLogout}
                  >
                    <ListItemIcon
                      className={`sidebar-icon ${isActive ? "active" : ""}`}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText primary={item.text} />}
                  </ListItem>
                ) : (
                  <Link
                    to={item.path}
                    className={`sidebar-link ${isActive ? "active" : ""}`}
                  >
                    <ListItem button className="sidebar-list-item">
                      <ListItemIcon
                        className={`sidebar-icon ${isActive ? "active" : ""}`}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {isDrawerOpen && <ListItemText primary={item.text} />}
                    </ListItem>
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </PerfectScrollbar>
    </Drawer>
  );
};

export default SideBar;
